import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, LabelList } from 'recharts';
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from "ui/components/card";
import { BARCHART } from 'icons';
import { Icon } from 'components';
import { formatHoursToTime } from 'utils/timesheet';
import ChartCardSkeleton from './chart-loader';

export const TeamUtilizationStackedChart = ({ data, isLoading, error, title }) => {
    // Define colors matching the reference image exactly
    const colors = {
        available: '#075985',
        client: '#BAE6FD',
        internal: '#0EA5E9',
        presales: '#BEF264',
        free: '#F59E0B'
    };

    // Transform data for the chart
    const transformData = (data) => {
        if (!data) return [];

        const availableTotal = data.available_hours?.hours || 0;
        const loggedData = data.logged_hours || {};
        const approvedData = data.approved_hours || {};
        const freeData = data.free_hours || {};

        return [
            {
                name: 'Available',
                available: availableTotal,
                total: availableTotal
            },
            {
                name: 'Logged',
                client: loggedData.client_project_hours || 0,
                internal: loggedData.internal_project_hours || 0,
                presales: loggedData.presales_project_hours || 0,
                total: (loggedData.client_project_hours || 0) +
                    (loggedData.internal_project_hours || 0) +
                    (loggedData.presales_project_hours || 0)
            },
            {
                name: 'Approved',
                client: approvedData.client_project_hours || 0,
                internal: approvedData.internal_project_hours || 0,
                presales: approvedData.presales_project_hours || 0,
                total: (approvedData.client_project_hours || 0) +
                    (approvedData.internal_project_hours || 0) +
                    (approvedData.presales_project_hours || 0)
            },
            {
                name: 'Free',
                free: freeData.hours || 0,
                total: freeData.hours || 0
            }
        ];
    };

    const CustomLabel = (props) => {
        const { x, y, width, height, value } = props;

        if (!value || value === 0) return null;

        return (
            <text
                x={x + width / 2}
                y={y + height / 2}
                fill="#000"
                textAnchor="middle"
                dominantBaseline="middle"
                fontSize="13"
                fontWeight="500"
            >
                {formatHoursToTime(value)}
            </text>
        );
    };

    const CustomTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            const item = payload[0].payload;
            return (
                <div className="rounded-lg border bg-white p-3 shadow-lg">
                    <p className="font-semibold text-sm mb-2">{item.name}</p>
                    <div className="space-y-1">
                        {payload.map((entry, index) => (
                            entry.value > 0 && entry.dataKey !== 'total' && (
                                <div key={index} className="flex items-center justify-between gap-4 text-xs">
                                    <div className="flex items-center gap-2">
                                        <div
                                            className="h-2 w-2 rounded-full"
                                            style={{ backgroundColor: entry.fill }}
                                        />
                                        <span className="text-gray-600 capitalize">{entry.dataKey}:</span>
                                    </div>
                                    <span className="font-medium">{formatHoursToTime(entry.value)}</span>
                                </div>
                            )
                        ))}
                    </div>
                    {item.total > 0 && (
                        <div className="mt-2 pt-2 border-t flex items-center justify-between text-xs">
                            <span className="font-semibold">Total:</span>
                            <span className="font-semibold">{formatHoursToTime(item.total)}</span>
                        </div>
                    )}
                </div>
            );
        }
        return null;
    };

    if (isLoading) {
        return (
            <ChartCardSkeleton />
        );
    }

    if (error) {
        return (
            <Card className="w-full">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <div className="flex items-center gap-2">
                        <Icon icon={BARCHART} className="size-4 bg-white" />
                        <CardTitle className="text-base font-medium">{title}</CardTitle>
                    </div>
                </CardHeader>
                <CardContent>
                    <div className="flex h-[400px] items-center justify-center">
                        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
                            <p className="text-sm text-red-600 font-medium">Error: {error?.message || error?.toString() || "Something went wrong"}</p>
                        </div>
                    </div>
                </CardContent>
            </Card>
        );
    }

    const chartData = transformData(data);

    return (
        <Card className="mx-6 mt-4 mb-6 p-0 gap-0 border-1 radius-xl">
            <CardHeader className="flex flex-row items-center justify-between !p-4 border-b bg-white">
                <div className="flex items-center gap-2">
                    <Icon icon={BARCHART} className="size-4 bg-white" />
                    <div className="text-muted-foreground">{title}</div>
                </div>
                <div className="flex flex-wrap items-center gap-4 text-xs">
                    <div className="flex items-center gap-1.5">
                        <div className="h-3 w-3 rounded-xs" style={{ backgroundColor: colors.available }} />
                        <span className="text-gray-600">Available</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <div className="h-3 w-3 rounded-xs" style={{ backgroundColor: colors.client }} />
                        <span className="text-gray-600">Client</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <div className="h-3 w-3 rounded-xs" style={{ backgroundColor: colors.internal }} />
                        <span className="text-gray-600">Internal</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <div className="h-3 w-3 rounded-xs" style={{ backgroundColor: colors.presales }} />
                        <span className="text-gray-600">Pre-Sales</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <div className="h-3 w-3 rounded-xs" style={{ backgroundColor: colors.free }} />
                        <span className="text-gray-600">Free</span>
                    </div>
                </div>
            </CardHeader>
            <CardContent className="pt-6">
                <ResponsiveContainer width="100%" height={400}>
                    <BarChart
                        data={chartData}
                        margin={{ top: 10, right: 10, left: 0, bottom: 30 }}
                        barCategoryGap="20%"
                    >
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                        <XAxis
                            dataKey="name"
                            axisLine={false}
                            tickLine={false}
                            tick={{ fill: '#6b7280', fontSize: 13 }}
                            dy={10}
                        />
                        <YAxis
                            axisLine={false}
                            tickLine={false}
                            tick={{ fill: '#6b7280', fontSize: 13 }}
                            dx={-10}
                        />
                        <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0, 0, 0, 0.05)' }} />

                        <Bar dataKey="available" stackId="a" fill={colors.available}>
                            <LabelList dataKey="available" content={<CustomLabel />} />
                        </Bar>
                        <Bar dataKey="client" stackId="a" fill={colors.client}>
                            <LabelList dataKey="client" content={<CustomLabel />} />
                        </Bar>
                        <Bar dataKey="internal" stackId="a" fill={colors.internal}>
                            <LabelList dataKey="internal" content={<CustomLabel />} />
                        </Bar>
                        <Bar dataKey="presales" stackId="a" fill={colors.presales}>
                            <LabelList dataKey="presales" content={<CustomLabel />} />
                        </Bar>
                        <Bar dataKey="free" stackId="a" fill={colors.free}>
                            <LabelList dataKey="free" content={<CustomLabel />} />
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </CardContent>
        </Card>
    );
};